/* eslint-disable no-console */
import posthog from 'posthog-js'
import { useCallback, useEffect, useState } from 'react'
import { config, getClientHeaders } from '../../../config'
import { UserContext, UserToken } from '../../../types'
import { SESSION_STORAGE_KEY } from '../../analytics/use-event/use-event'
import { useSafeGetUserData } from './use-safe-get-user-data'

type UseGetUserDataParams = {
  userToken: UserToken
  getValidToken?: () => Promise<UserToken>
  forceRefresh?: () => Promise<void>
}

interface UseGetUserData {
  user: UserContext | null
  error: boolean
  loading: boolean
  loadingUserData: boolean
  isFetchingUserData: () => boolean
  retry: () => Promise<void>
  silentRetry: () => void
  posthogFeatureFlags: any
  posthogFlagsLoaded: boolean
}

export const useGetUserData = ({
  userToken,
  getValidToken,
  forceRefresh
}: UseGetUserDataParams): UseGetUserData => {
  const clientHeaders = getClientHeaders()
  const [silentRetrying, setSilentRetrying] = useState(false)
  const [posthogFlagsLoaded, setPosthogFlagsLoaded] = useState<boolean>(false)
  const apiUrl = config.graphql.endpoint

  const [{ loading, error, value }, retry] = useSafeGetUserData({
    apiUrl,
    clientHeaders,
    getValidToken: getValidToken ?? (async () => userToken),
    forceRefresh
  })

  const userId = value?.user?.id

  const [lastUsedToken, setLastUsedToken] = useState<string | null>(null)
  const [posthogFeatureFlags, setPosthogFeatureFlags] = useState<any>(null)

  useEffect(() => {
    if (userToken && userToken !== lastUsedToken) {
      setLastUsedToken(userToken)
      retry()
    }
  }, [userToken, lastUsedToken, retry])

  useEffect(() => {
    if (!userToken || !userId) return

    const unsubscribe = posthog.onFeatureFlags(async (_flags, variants) => {
      try {
        const merged: Record<string, unknown> = { ...variants }

        await Promise.all(
          Object.keys(variants).map(async (key) => {
            const browserPayload =
              typeof posthog.getFeatureFlagPayload === 'function'
                ? posthog.getFeatureFlagPayload(key)
                : undefined

            if (browserPayload !== undefined && browserPayload !== null) {
              merged[key] = browserPayload
              return
            }

            if (typeof (posthog as any).getRemoteConfigPayload === 'function') {
              try {
                const remotePayload = await (
                  posthog as any
                ).getRemoteConfigPayload(key)
                if (remotePayload !== undefined && remotePayload !== null) {
                  merged[key] = remotePayload
                }
              } catch (e) {
                console.warn(`Failed to get remoteConfig payload for ${key}`, e)
              }
            }
          })
        )

        setPosthogFeatureFlags(merged)
        setPosthogFlagsLoaded(true)

        try {
          if (
            typeof window !== 'undefined' &&
            window.sessionStorage.getItem(SESSION_STORAGE_KEY)
          ) {
            console.log('PostHog feature flags (merged):', _flags, merged)
          }
        } catch {
          console.warn('Failed to get remoteConfig payload for', _flags, merged)
        }
      } catch (e) {
        console.error(e)
        console.warn('Failed to get remoteConfig payload for', _flags, variants)
        setPosthogFeatureFlags(variants)
        setPosthogFlagsLoaded(true)
      }
    })

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribe?.()
    }
  }, [userToken, userId])

  const silentRetry = useCallback(() => {
    setSilentRetrying(true)
    retry()
  }, [retry])

  useEffect(() => {
    // reset "silentRetrying" state after fetching the data
    if (loading === false && userToken) {
      setSilentRetrying(false)
    }
  }, [loading, userToken])

  const isFetchingUserData = useCallback(() => {
    return loading === true || silentRetrying === true
  }, [loading, silentRetrying])

  return {
    user: value?.user,
    error: !!error,
    retry,
    silentRetry,
    loadingUserData: loading,
    isFetchingUserData,
    // bypass default loading state if "silentRetrying" is true
    loading: silentRetrying ? false : loading || !userToken,
    posthogFeatureFlags,
    posthogFlagsLoaded
  }
}
